import { FEATURE_KEYS } from "../../access-control/models/role-feature.models";
import type { AdminMonitoringSectionId } from "./admin.models";

export type CronJobLogEnvironment = "DEV" | "QAS" | "PRD";
export type CronJobLogSortBy = "created" | "name" | "jobFrequency" | "run";
export type CronJobLogSortDirection = "asc" | "desc";

export interface CronJobLogMetricMap {
  [key: string]: string | number | boolean;
}

export interface CronJobLogListItem {
  id: string;
  created: string;
  name: string;
  run: boolean;
  jobFrequency: string | null;
  summary: string | null;
  hasDetails: boolean;
  hasErrors: boolean;
  errorCount: number;
  metrics: CronJobLogMetricMap;
}

export interface CronJobLogDetail extends CronJobLogListItem {
  details: unknown;
  errors: unknown;
  rawDetails: string | null;
  rawErrors: string | null;
}

export interface CronJobTypeSummary {
  name: string;
  total: number;
  errorCount: number;
  liveRuns: number;
  latestCreated: string | null;
}

export interface CronJobLogSummary {
  totalLogs: number;
  logsWithErrors: number;
  liveRuns: number;
  dryRuns: number;
  jobTypes: number;
  latestCreated: string | null;
}

export interface CronJobLogDailyCount {
  day: string;
  total: number;
  errors: number;
}

export interface CronJobLogsResponse {
  environment: CronJobLogEnvironment;
  entries: CronJobLogListItem[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasMore: boolean;
  nextPage: number | null;
  prevPage: number | null;
  summary: CronJobLogSummary;
  jobTypes: CronJobTypeSummary[];
  dailyCounts: CronJobLogDailyCount[];
}

export interface CronJobLogsQueryParams {
  environment: CronJobLogEnvironment;
  page: number;
  pageSize: number;
  name?: string;
  run?: boolean;
  jobFrequency?: string;
  sortBy: CronJobLogSortBy;
  sortDirection: CronJobLogSortDirection;
}

export type AdminFeatureMonitoringSectionId = Exclude<AdminMonitoringSectionId, "cronJobs" | "uwhc">;

export type AdminMonitoringTone = "success" | "warning" | "danger" | "neutral";

export interface AdminMonitoringKpi {
  key: string;
  label: string;
  value: string | number;
  caption?: string;
  tone?: AdminMonitoringTone;
}

export interface AdminMonitoringBreakdownItem {
  label: string;
  value: number;
  secondaryValue?: number;
  tone?: AdminMonitoringTone;
}

export interface AdminMonitoringBreakdown {
  key: string;
  title: string;
  kind: "bar" | "stack" | "list";
  items: AdminMonitoringBreakdownItem[];
}

export interface AdminMonitoringTableColumn {
  key: string;
  label: string;
}

export interface AdminMonitoringTable {
  key: string;
  title: string;
  columns: AdminMonitoringTableColumn[];
  rows: Array<Record<string, string | number | null>>;
}

export interface AdminFeatureMonitoringResponse {
  environment: CronJobLogEnvironment;
  title: string;
  summary: string;
  updatedAt: string;
  kpis: AdminMonitoringKpi[];
  breakdowns: AdminMonitoringBreakdown[];
  tables: AdminMonitoringTable[];
  signals: string[];
}

export type TechFormInboxOtfStatus = "saved" | "not_saved" | "broken" | "not_otf" | "unknown";

export type TechFormInboxEmailStatus = TechFormInboxOtfStatus | "no_json";

export interface TechFormInboxEmailPreview {
  id: string;
  subject: string;
  from: string;
  receivedDateTime: string;
  hasAttachments: boolean;
  bodyPreview?: string;
  bodyJsonWarning?: string;
  otfStatus?: TechFormInboxEmailStatus;
  otfSummary?: string;
}

export interface TechFormInboxBodyPreview {
  messageId: string;
  body: string;
  contentType: string;
  bodyJsonWarning?: string;
}

export interface TechFormInboxPayloadSummary {
  offlineId: string | null;
  imoNumber: string | null;
  vesselName: string | null;
  populatedSections: string[];
  contactCount: number;
  draftRowCount: number;
  portCallCount: number;
  powerManagementRowCount: number;
}

export interface TechFormInboxSubmissionSummary {
  vesselName: string | null;
  vesselMasterEmail: string | null;
  submitStatus: string | null;
  submittedOn: string | null;
  saveStrategy: string | null;
  submittedByEmail: boolean;
  storedJsonStatus: "missing" | "matches_email" | "different" | "unreadable";
}

export interface TechFormInboxSectionSaveHealth {
  checked: boolean;
  requiredSections: string[];
  savedSections: string[];
  missingSections: string[];
  complete: boolean;
  error?: string;
}

export interface TechFormInboxJsonPreview {
  messageId: string;
  attachmentId: string;
  subject: string;
  from: string;
  receivedDateTime: string;
  attachmentName: string;
  contentType?: string;
  downloadedBytes?: number;
  imoNumber?: string;
  matchesExpectedFileName: boolean;
  status: "parsed" | "unreadable";
  json?: unknown;
  otfStatus?: TechFormInboxOtfStatus;
  otfStatusReason?: string;
  warnings?: string[];
  payloadSummary?: TechFormInboxPayloadSummary;
  submissionSummary?: TechFormInboxSubmissionSummary;
  sectionSaveHealth?: TechFormInboxSectionSaveHealth;
  reingestionRequired?: boolean;
  stage?: string;
  httpStatus?: number;
  reason?: string;
}

export interface TechFormInboxPreviewResponse {
  environment: CronJobLogEnvironment;
  mailbox: string;
  imoNumber?: string;
  summary: string;
  health: {
    ok: boolean;
    messagesScanned: number;
    candidateAttachments: number;
    parsedAttachments: number;
    unreadableAttachments: number;
    snowflakeCorrelationOk: boolean;
    snowflakeCorrelationError?: string;
    savedAttachments: number;
    notSavedAttachments: number;
    brokenAttachments: number;
    nonOtfAttachments: number;
    unknownAttachments: number;
    savedEmails: number;
    nonOtfEmails: number;
    reingestionRequiredEmails: number;
    reingestionAllowed: boolean;
    runtimeEnvironment: CronJobLogEnvironment;
  };
  results: TechFormInboxJsonPreview[];
  messages: TechFormInboxEmailPreview[];
  pagination: {
    pageSize: number;
    nextCursor?: string;
    hasNext: boolean;
  };
  dailyCounts: Array<{
    date: string;
    receivedEmails: number;
    savedOtfEmails: number;
    reingestionRequiredEmails: number;
  }>;
}

export interface ReingestTechFormInboxRequest {
  mailbox: string;
  environment: CronJobLogEnvironment;
  targets: Array<{ messageId: string; attachmentId: string }>;
}

export interface ReingestTechFormInboxResponse {
  environment: CronJobLogEnvironment;
  mailbox: string;
  summary: string;
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  results: Array<{
    messageId: string;
    attachmentId: string;
    attachmentName: string;
    imoNumber: string;
    status: "processed" | "skipped" | "failed";
    reason?: string;
  }>;
}

export const ADMIN_MONITORING_ENDPOINTS: Record<AdminFeatureMonitoringSectionId, string> = {
  documents: "documents",
  vessels: "vessels",
  techForms: "tech-forms",
  balticQuestionnaires: "baltic-questionnaires",
  voyages: "voyages",
  voyagePnl: "voyage-pnl",
  notifications: "notifications",
};

export const CRON_JOB_LOG_ENVIRONMENTS: Array<{ value: CronJobLogEnvironment; label: string; database: string }> = [
  { value: "DEV", label: "Dev", database: "DEV" },
  { value: "QAS", label: "QS", database: "QAS" },
  { value: "PRD", label: "PRD", database: "PRD" },
];

export interface CronMonitoringJobDefinition {
  name: string;
  title: string;
  group: string;
  focus: string;
  metricKeys: string[];
  metricLimit?: number;
  requiredFeatureKey?: string;
  renderer?: "generic" | "holdCleaning";
}

export const CRON_MONITORING_JOB_GROUPS = [
  "Accounts",
  "Forms",
  "Documents",
  "Voyage PnL",
  "Hull cleaning",
  "Operations",
  "Hold Cleaning",
] as const;

export const CRON_MONITORING_JOBS: CronMonitoringJobDefinition[] = [
  {
    name: "HOLD_CLEANING_REFRESH",
    title: "Hold Cleaning refresh",
    group: "Hold Cleaning",
    focus: "PostgreSQL-backed run state, source checkpoints, errors, duration, and crew-import quarantine.",
    metricKeys: [],
    requiredFeatureKey: FEATURE_KEYS.HOLD_CLEANING_ADMIN,
    renderer: "holdCleaning",
  },
  {
    name: "CREATE_EXT_ACCS",
    title: "Create external accounts",
    group: "Accounts",
    focus: "Onboarding candidates, vessels missing emails, and account creation failures.",
    metricKeys: ["onboardingCandidatesNo", "vesselsToOnboardNo", "missingEmailsNo", "onboardedVesselsNo"],
  },
  {
    name: "CHECK_EXT_ACC_ACTIVATED",
    title: "Check account activation",
    group: "Accounts",
    focus: "Accounts still inactive, newly activated accounts, and DXO lookup failures.",
    metricKeys: ["inactiveVesselsNo", "activatedVesselsNo"],
  },
  {
    name: "REINVITE_PROVISIONED_ACCOUNTS",
    title: "Reinvite provisioned accounts",
    group: "Accounts",
    focus: "Provisioned accounts that would receive or did receive a reinvite.",
    metricKeys: ["allAccountsNo", "provisionedNo"],
  },
  {
    name: "CREATE_TECH_FORM",
    title: "Create Tech Forms",
    group: "Forms",
    focus: "Tech Form candidates, missing vessel emails, and created request rows.",
    metricKeys: ["tfCandidatesNo", "tfsToCreateNo", "missingEmailsNo", "createdTFsNo"],
  },
  {
    name: "REJOIN_FLEET_TECH_FORMS",
    title: "Rejoin Fleet Tech Forms",
    group: "Forms",
    focus: "Fresh Tech Forms created for vessels returning under a different time-charter contract.",
    metricKeys: ["tfsToResetNo"],
  },
  {
    name: "SEND_TECH_FORM_REMINDER",
    title: "Send Tech Form reminders",
    group: "Forms",
    focus: "Pending Tech Forms considered for vessel-master reminder emails.",
    metricKeys: ["techFormsNo"],
  },
  {
    name: "PROCESS_CREW_REPORT_ONBOARDING",
    title: "Process Crew Reports",
    group: "Forms",
    focus:
      "Mailbox import, Master correction guidance, Period-vessel onboarding and long-tenure refresh campaigns, requests, chasers, responses, separate Ops Support escalations, and delivery failures.",
    metricKeys: [
      "mailboxCandidateEmails",
      "periodVesselsProcessed",
      "initialRequestsDue",
      "initialRequestsAttempted",
      "chasersDue",
      "chasersAttempted",
      "masterFeedbackEnabled",
      "masterFeedbackEligibleAttachments",
      "masterFeedbackRecipients",
      "masterFeedbackPendingBatchesFound",
      "masterFeedbackPendingBatchesDeferred",
      "masterFeedbackAttempts",
      "masterFeedbackDeliveryConfirmed",
      "masterFeedbackDeliveryUnconfirmed",
      "longTenureCandidateVessels",
      "longTenureCrewMembersDiscovered",
      "longTenureCampaignsToStart",
      "longTenureCampaignsStarted",
      "longTenureManualRequestsToAdopt",
      "longTenureManualRequestsAdopted",
      "longTenureInitialRequestsDue",
      "longTenureInitialRequestsAttempted",
      "longTenureChasersDue",
      "longTenureChasersAttempted",
      "longTenureMissingEmails",
      "longTenureInvalidEmails",
      "longTenureResponsesImported",
      "longTenureStillOverdueEscalationsDue",
      "longTenureStillOverdueEscalationsCreated",
      "longTenureNoResponseEscalationsDue",
      "longTenureNoResponseEscalationsCreated",
      "longTenureNotificationDeliveryUnconfirmed",
      "longTenureErrors",
      "missingEmails",
      "previewErrors",
    ],
    metricLimit: 35,
  },
  {
    name: "CREATE_BALTIC_FORM",
    title: "Create Baltic Questionnaires",
    group: "Forms",
    focus: "Baltic Questionnaire candidates, missing vessel emails, created rows, and Q88 imports.",
    metricKeys: ["bqCandidatesNo", "bqsToCreateNo", "missingEmailsNo", "createdBQsNo", "importsCount"],
  },
  {
    name: "SEND_BALTIC_FORM_REMINDER",
    title: "Send Baltic reminders",
    group: "Forms",
    focus: "Pending Baltic Questionnaires considered for reminder emails.",
    metricKeys: ["balticFormsNo"],
  },
  {
    name: "IMPORT_BALTIC_QUESTIONNAIRE",
    title: "Import Baltic Questionnaire",
    group: "Forms",
    focus: "Manual Q88 Baltic Questionnaire import runs.",
    metricKeys: [],
  },
  {
    name: "SEND_DOC_REQUEST_REMINDER",
    title: "Send missing-document reminders",
    group: "Documents",
    focus: "Pending document requests grouped into reminder emails.",
    metricKeys: ["docsNo"],
  },
  {
    name: "CREATE_ONBOARDING_CERTIFICATE_REQUESTS",
    title: "Create onboarding certificate requests",
    group: "Documents",
    focus:
      "Onboarding certificate request candidates, name-change candidates, skipped vessels, created rows, and sent emails.",
    metricKeys: [
      "onboardingCandidatesNo",
      "nameChangeCandidatesNo",
      "vesselsToRequestNo",
      "missingEmailsNo",
      "createdRequestsNo",
      "sentEmailsNo",
      "skippedAlreadyRequestedNo",
      "skippedDuplicateCandidatesNo",
      "retriedIncompleteRequestsNo",
      "requestRowsInsertedNo",
    ],
  },
  {
    name: "SEND_DOC_RENEWAL_REMINDER",
    title: "Send renewal reminders",
    group: "Documents",
    focus: "Document renewal candidates and Baltic renewal side effects.",
    metricKeys: ["docsNo"],
  },
  {
    name: "SNAPSHOT_CERTIFICATES_KPIS",
    title: "Store certificate KPI snapshot",
    group: "Documents",
    focus: "Daily persisted Period Fleet Certificates and Period Fleet Baltic Questionnaire KPI metrics.",
    metricKeys: ["snapshotDate", "periodFleetVesselsTotal"],
  },
  {
    name: "LOAD_VOYAGE_PNL_DATA",
    title: "Load Voyage PnL data",
    group: "Voyage PnL",
    focus: "Voyage PnL summary rows loaded and Snowflake write failures.",
    metricKeys: ["inserted", "success"],
  },
  {
    name: "CREATE_VESSEL_ASSESMENT_NOTIF",
    title: "Create assessment notifications",
    group: "Voyage PnL",
    focus: "Created, duplicate, and failed voyage-assessment notifications.",
    metricKeys: ["createdCount", "duplicateCount", "failedCount"],
  },
  {
    name: "SEND_VOYAGE_PNL_REMINDER",
    title: "Send Voyage PnL reminders",
    group: "Voyage PnL",
    focus: "Voyage PnL email candidates, sent messages, skipped voyages, and failures.",
    metricKeys: ["candidateCount", "attemptedCount", "sentCount", "skippedCount", "failedCount"],
  },
  {
    name: "EXPIRE_VESSEL_ASSESMENT_NOTIF",
    title: "Expire assessment notifications",
    group: "Voyage PnL",
    focus: "Stale vessel-assessment notifications targeted for expiry.",
    metricKeys: ["notifsNo"],
  },
  {
    name: "HULL_CLEANING_REQUEST_CREATED",
    title: "Process hull-cleaning emails",
    group: "Hull cleaning",
    focus: "Outbox rows, request matches, context loads, sent emails, and skipped emails.",
    metricKeys: [
      "processedRequestsNo",
      "matchedRequestRowsNo",
      "loadedDryDockRowsNo",
      "loadedHullEventRowsNo",
      "sentEmailsNo",
      "skippedEmailsNo",
    ],
  },
  {
    name: "SEND_OPS_DAILY_EMAILS",
    title: "Send Ops daily emails",
    group: "Operations",
    focus: "Daily operations campaign emails, duplicate-protected sends, morning summary delivery, and failures.",
    metricKeys: ["sentEmailsNo", "summarySent"],
  },
];
